/*
 * Reportes recurrentes (ficha 190): Descuentos, Incidencias y Liquidaciones aplicados en nómina.
 * Modal común para las tres páginas: el usuario elige un rango de fechas (se consideran los
 * Periodos de Nómina que caen completos dentro del rango), conceptos y, opcionalmente, tipos de
 * nómina; el backend devuelve las filas de nóminas Autorizadas y aquí se arma un Excel con una
 * hoja de detalle y otra de resumen por concepto y periodo.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Form, DatePicker, Button, Typography, message,
} from 'antd';
import { useSelector } from 'react-redux';
import ExcelJS from 'exceljs';
import Select from 'components/Select';
import { getReporteRecurrente } from 'api/nominas/reportesRecurrentes';
import { getTiposDeNominasEmpleados } from 'api/nominas/catalogos/tiposNominasEmpleados';
import {
  FORMATO_MONEDA,
  FORMATO_FECHA,
  aplicarEncabezado,
  descargarWorkbook,
  escribirLineasEncabezado,
  escribirTabla,
} from 'utils/excelReportes';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const FORMATO_FECHA_API = 'YYYY-MM-DD';

// Columnas comunes a los tres reportes (mismos nombres que devuelve el backend)
const COLUMNAS_INICIALES = [
  { header: 'Periodo', key: 'periodo', width: 12 },
  {
    header: 'Fecha Inicial', key: 'periodo_fecha_inicial', width: 13, fecha: true,
  },
  {
    header: 'Fecha Final', key: 'periodo_fecha_final', width: 13, fecha: true,
  },
  { header: 'Tipo de Nómina', key: 'tipo_de_nomina_empleado', width: 24 },
  { header: 'Subtipo', key: 'subtipo_de_nomina', width: 14 },
  { header: 'Folio Nómina', key: 'folio_nomina', width: 14 },
  { header: 'No. Empleado', key: 'numero_de_empleado', width: 13 },
  { header: 'Empleado', key: 'empleado', width: 40 },
  { header: 'RFC', key: 'rfc', width: 16 },
  { header: 'Plaza', key: 'plaza', width: 36 },
  { header: 'Puesto', key: 'puesto', width: 30 },
  { header: 'Área', key: 'area', width: 36 },
  { header: 'Clave', key: 'concepto_clave', width: 12 },
];

const ReporteRecurrenteModal = ({
  open,
  onClose,
  tipo,
  titulo,
  conceptos,
  conceptoLabel,
  columnasConcepto,
  fileName,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tiposDeNomina, setTiposDeNomina] = useState([]);
  const entidad = useSelector((state) => state?.auth?.entidad);

  useEffect(() => {
    let mounted = true;
    if (open && !tiposDeNomina.length) {
      (async () => {
        const res = await getTiposDeNominasEmpleados();
        if (mounted) setTiposDeNomina(res?.results || res || []);
      })();
    }
    return () => { mounted = false; };
  }, [open]);

  const construirExcel = (data, valores) => {
    const { periodos, filas } = data;
    const workbook = new ExcelJS.Workbook();
    const columnas = [...COLUMNAS_INICIALES, ...columnasConcepto];

    const conceptosSeleccionados = conceptos
      .filter((c) => (valores.conceptos || []).includes(c.id))
      .map((c) => c.label);
    const tiposSeleccionados = tiposDeNomina
      .filter((t) => (valores.tipos_de_nomina_empleado || []).includes(t.id))
      .map((t) => t.descripcion);

    const encabezado = [
      'SISTEMA DE NÓMINAS',
      titulo.toUpperCase(),
      `Entidad: ${entidad?.nombre || ''}`,
      `Rango: ${valores.rango[0].format(FORMATO_FECHA)} al ${valores.rango[1].format(FORMATO_FECHA)}`,
      `Periodos de Nómina: ${periodos.map((p) => p.numero_de_nomina).join(', ')}`,
      `${conceptoLabel}: ${conceptosSeleccionados.length ? conceptosSeleccionados.join(', ') : 'Todos'}`,
      `Tipos de Nómina: ${tiposSeleccionados.length ? tiposSeleccionados.join(', ') : 'Todos'}`,
      'Solo se consideran nóminas Autorizadas.',
    ];
    // --- Hoja DETALLE ---
    const wsDetalle = workbook.addWorksheet('Detalle');
    escribirLineasEncabezado(wsDetalle, encabezado);
    escribirTabla(wsDetalle, columnas, filas);

    // --- Hoja RESUMEN: total de importe por concepto y periodo ---
    const wsResumen = workbook.addWorksheet('Resumen');
    escribirLineasEncabezado(wsResumen, encabezado);
    const periodosConDatos = periodos.filter((p) => filas.some((f) => f.periodo_id === p.id));
    aplicarEncabezado(wsResumen.addRow([
      'Clave', 'Concepto', ...periodosConDatos.map((p) => p.numero_de_nomina), 'TOTAL', 'No. de Registros',
    ]));

    const resumen = new Map();
    filas.forEach((f) => {
      const llave = `${f.concepto_clave}|${f.concepto}`;
      if (!resumen.has(llave)) {
        resumen.set(llave, {
          clave: f.concepto_clave, concepto: f.concepto, porPeriodo: {}, total: 0, registros: 0,
        });
      }
      const r = resumen.get(llave);
      const importe = Number(f.importe) || 0;
      r.porPeriodo[f.periodo_id] = (r.porPeriodo[f.periodo_id] || 0) + importe;
      r.total += importe;
      r.registros += 1;
    });

    const r2 = (n) => Math.round(n * 100) / 100;
    const totales = { porPeriodo: {}, total: 0, registros: 0 };
    [...resumen.values()]
      .sort((a, b) => String(a.clave).localeCompare(String(b.clave)))
      .forEach((r) => {
        const row = wsResumen.addRow([
          r.clave,
          r.concepto,
          ...periodosConDatos.map((p) => r2(r.porPeriodo[p.id] || 0)),
          r2(r.total),
          r.registros,
        ]);
        for (let col = 3; col <= periodosConDatos.length + 3; col += 1) {
          row.getCell(col).numFmt = FORMATO_MONEDA;
        }
        periodosConDatos.forEach((p) => {
          totales.porPeriodo[p.id] = (totales.porPeriodo[p.id] || 0) + (r.porPeriodo[p.id] || 0);
        });
        totales.total += r.total;
        totales.registros += r.registros;
      });
    const filaTotal = wsResumen.addRow([
      '',
      'TOTAL',
      ...periodosConDatos.map((p) => r2(totales.porPeriodo[p.id] || 0)),
      r2(totales.total),
      totales.registros,
    ]);
    filaTotal.font = { bold: true };
    for (let col = 3; col <= periodosConDatos.length + 3; col += 1) {
      filaTotal.getCell(col).numFmt = FORMATO_MONEDA;
    }
    wsResumen.getColumn(1).width = 12;
    wsResumen.getColumn(2).width = 45;
    for (let col = 3; col <= periodosConDatos.length + 4; col += 1) {
      wsResumen.getColumn(col).width = 16;
    }

    return workbook;
  };

  const onGenerar = async () => {
    try {
      const valores = await form.validateFields();
      setLoading(true);
      const data = await getReporteRecurrente(tipo, {
        fecha_inicial: valores.rango[0].format(FORMATO_FECHA_API),
        fecha_final: valores.rango[1].format(FORMATO_FECHA_API),
        conceptos: valores.conceptos,
        tipos_de_nomina_empleado: valores.tipos_de_nomina_empleado,
      });
      if (!data) {
        setLoading(false);
        return;
      }
      if (!data.periodos?.length) {
        message.info('No hay Periodos de Nómina que caigan completos dentro del rango seleccionado.');
      } else if (!data.filas?.length) {
        message.info('No se encontró información en nóminas Autorizadas con los filtros seleccionados.');
      } else {
        const workbook = construirExcel(data, valores);
        const desde = valores.rango[0].format('DD-MM-YYYY');
        const hasta = valores.rango[1].format('DD-MM-YYYY');
        await descargarWorkbook(workbook, `${fileName}_${desde}_al_${hasta}.xlsx`);
      }
      setLoading(false);
    } catch (err) {
      // Errores de validación del formulario: antd ya los muestra en el campo
      setLoading(false);
    }
  };

  return (
    <Modal
      title={titulo}
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancelar" onClick={onClose}>Cancelar</Button>,
        <Button key="generar" type="primary" loading={loading} onClick={onGenerar}>
          Generar Excel
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="rango"
          label="Periodo"
          rules={[{ required: true, message: 'El campo es requerido' }]}
          extra="Se consideran los Periodos de Nómina que caen completos dentro del rango."
        >
          <RangePicker format={FORMATO_FECHA} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="conceptos" label={conceptoLabel} extra="Vacío = todos.">
          <Select
            mode="multiple"
            dataSource={conceptos}
            keyProp="id"
            render={(item) => item.label}
            allowClear
          />
        </Form.Item>
        <Form.Item name="tipos_de_nomina_empleado" label="Tipos de Nómina" extra="Vacío = todos.">
          <Select
            mode="multiple"
            dataSource={tiposDeNomina}
            render={(item) => item.descripcion}
            allowClear
          />
        </Form.Item>
        <Text type="secondary">Solo se consideran nóminas Autorizadas.</Text>
      </Form>
    </Modal>
  );
};

ReporteRecurrenteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tipo: PropTypes.oneOf(['descuentos', 'incidencias', 'liquidaciones']).isRequired,
  titulo: PropTypes.string.isRequired,
  // [{ id, label }]
  conceptos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
  })),
  conceptoLabel: PropTypes.string,
  // Columnas propias de cada reporte, después de las comunes:
  // { header, key, width, numFmt?, fecha? }
  columnasConcepto: PropTypes.arrayOf(PropTypes.shape({
    header: PropTypes.string,
    key: PropTypes.string,
    width: PropTypes.number,
    numFmt: PropTypes.string,
    fecha: PropTypes.bool,
  })).isRequired,
  fileName: PropTypes.string.isRequired,
};

ReporteRecurrenteModal.defaultProps = {
  conceptos: [],
  conceptoLabel: 'Conceptos',
};

export { FORMATO_MONEDA };
export default ReporteRecurrenteModal;
